
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { zodResolver } from '@hookform/resolvers/zod';
import { Controller, useForm } from 'react-hook-form';
import { Button, CustomSelect, RichTextEditor, SaveDraftModal, Toggle } from '@/components/ui';
import { Heading, Paragraph } from '@/components/ui/Typography';
import Input from '@/components/ui/Input/Input';
import TextArea from '@/components/ui/TextArea/TextArea';
import FileUpload from '@/components/ui/FileUpload';
import { useStrategicPillar, useCreateStrategicPillar, useUpdateStrategicPillar } from '@/lib/api/hooks/strategic-pillar';
import { useFileUpload } from '@/lib/api/hooks/file-upload';
import { StrategicPillarFormSchema } from '@/lib/api/types';
import { showToast } from '@/lib/toast';
import languageOptions from '@/utils/constants/languageOptions';

const DEFAULT_FORM_VALUES = {
  title: '',
  subtitle: '',
  summary: '',
  language: '',
  coverImageUrl: '',
  coverImageTitle: '',
  content: '',
  articleTag: '',
  landingPagePrioritiesOfAfsa: false,
  whoWeAre: false,
  memberCountries: false,
  history: false,
  leadership: false
};

const parseTagString = (value: string): string[] => value
  .split(',')
  .map((tag) => tag.trim())
  .filter(Boolean);


const StrategicPillarEditor: React.FC = () => {
  const { id } = useParams<{ id?: string }>();
  const navigate = useNavigate();
  const location = useLocation();

  const isEditMode = !!id && location.pathname.includes('/edit');
  const pillarId = isEditMode ? id : '';

  const { data: pillarData, isLoading: loadingPillar } = useStrategicPillar(pillarId || '');
  const { mutate: createPillar, isPending: isCreating } = useCreateStrategicPillar();
  const { mutate: updatePillar, isPending: isUpdating } = useUpdateStrategicPillar();
  const fileUploadMutation = useFileUpload();
  const fileUploadRef = useRef<HTMLDivElement>(null);

  const [localCoverFile, setLocalCoverFile] = useState<File | null>(null);
  const [localPreviewUrl, setLocalPreviewUrl] = useState<string>('');
  const [existingCoverUrl, setExistingCoverUrl] = useState<string>('');
  const [showUnsavedModal, setShowUnsavedModal] = useState(false);
  const [isFileUploading, setIsUploadingFile] = useState(false);
  const {
    control,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors, isDirty },
  } = useForm({
    resolver: zodResolver(StrategicPillarFormSchema),
    defaultValues: DEFAULT_FORM_VALUES,
    mode: "all"
  });

  const isSaving = isCreating || isUpdating;
  const isBusy = isSaving || isFileUploading;
  const watchTitle = watch('title');

  useEffect(() => {
    const shouldHydrateEditForm = isEditMode && pillarData?.data;
    if (shouldHydrateEditForm) {
      const article = pillarData.data as Record<string, unknown>;
      const tagValue = Array.isArray(article.articleTag)
        ? (article.articleTag as string[]).join(', ')
        : String(article.articleTag || '');
      reset({
        ...DEFAULT_FORM_VALUES,
        ...article,
        articleTag: tagValue,
        coverImageUrl: String(article.coverImageUrl || article.coverImage || ''),
      });
      setExistingCoverUrl(String(article.coverImageUrl || article.coverImage || ''));
    } else if (!isEditMode) {
      reset(DEFAULT_FORM_VALUES);
      setExistingCoverUrl('');
    }
    setLocalPreviewUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return '';
    });
    setLocalCoverFile(null);
  }, [isEditMode, pillarData, reset]);

  useEffect(() => {
    return () => {
      if (localPreviewUrl) {
        URL.revokeObjectURL(localPreviewUrl);
      }
    };
  }, [localPreviewUrl]);

  const handleBrowseFile = useCallback(() => {
    fileUploadRef.current?.click();
  }, []);

  const handleLocalRemove = useCallback(() => {
    setLocalPreviewUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return '';
    });
    setLocalCoverFile(null);
    setValue('coverImageUrl', existingCoverUrl || '', { shouldValidate: true });
  }, [existingCoverUrl, setValue]);

  const handleCoverFileChange = useCallback((files: File[] | null) => {
    if (!files || files.length === 0) {
      return;
    }
    const file = files[0];
    const objectUrl = URL.createObjectURL(file);
    setLocalPreviewUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return objectUrl;
    });
    setLocalCoverFile(file);
    setValue('coverImageUrl', objectUrl, { shouldValidate: true });
    if (!watch('coverImageTitle')) {
      setValue('coverImageTitle', file.name);
    }
  }, [setValue, watch]);

  const submitWithStatus = async (data: typeof DEFAULT_FORM_VALUES, saveStatus: 'DRAFT' | 'PUBLISH') => {
    let finalCoverImageUrl = data.coverImageUrl;
    if (localCoverFile) {
      try {
        setIsUploadingFile(true);
        const uploadResult = await fileUploadMutation.mutateAsync({ file: localCoverFile, category: 'IMAGE' });
        finalCoverImageUrl = uploadResult?.data?.fileUrl || '';
      } catch (error) {
        console.error(error);
        showToast.error('Cover image upload failed. Please try again.');
        return;
      } finally {
        setIsUploadingFile(false);
      }
    }
    if (!finalCoverImageUrl) {
      showToast.error('Please upload a cover image');
      return;
    }
    const tagsArray = parseTagString(data.articleTag || '');
    const payload = {
      title: data.title,
      subtitle: data.subtitle ?? '',
      summary: data.summary ?? '',
      language: data.language,
      coverImageUrl: finalCoverImageUrl,
      coverImageTitle: data.coverImageTitle || '',
      content: data.content,
      articleTag: tagsArray,
      landingPagePrioritiesOfAfsa: Boolean(data.landingPagePrioritiesOfAfsa),
      whoWeAre: Boolean(data.whoWeAre),
      memberCountries: Boolean(data.memberCountries),
      history: Boolean(data.history),
      leadership: Boolean(data.leadership),
      status: saveStatus,
    };

    if (isEditMode) {
      updatePillar(
        { strategicPillarId: pillarId as string, data: payload },
        {
          onSuccess: () => {
            if (saveStatus === 'PUBLISH') {
              showToast.publishSuccess({
                title: 'Publish Successful',
                message: 'You have successfully published your strategic pillar',
                onGoHome: () => {
                  navigate('/admin/strategic-pillars');
                }
              });
            } else {
              showToast.success('Strategic pillar updated successfully!');
              navigate('/admin/strategic-pillars');
            }
          },
          onError: (error) => {
            showToast.error('Failed to update: ' + (error.message || 'Unknown error'));
          }
        }
      );
    } else {
      createPillar(payload, {
        onSuccess: () => {
          if (saveStatus === 'PUBLISH') {
            showToast.publishSuccess({
              title: 'Publish Successful',
              message: 'You have successfully published your strategic pillar',
              onGoHome: () => {
                navigate('/admin/strategic-pillars');
              }
            });
          } else {
            showToast.success('Strategic pillar created as draft!');
            navigate('/admin/strategic-pillars');
          }
        },
        onError: (error) => {
          showToast.error('Failed to create: ' + (error.message || 'Unknown error'));
        }
      });
    }
  };

  const displayCoverImageUrl = localPreviewUrl || existingCoverUrl;
  const handleBackClick = useCallback(() => {
    if (isDirty || Boolean(localCoverFile)) {
      setShowUnsavedModal(true);
      return;
    }
    navigate('/admin/strategic-pillars');
  }, [isDirty, localCoverFile, navigate]);

  return (
    <div className="space-y-5 p-6 relative">
      <div className="flex items-center sticky top-0 z-10 bg-white justify-between">
        <Button
          type="button"
          variant="ghost"
          onClick={handleBackClick}
          className="!px-0 !py-0 text-[#047857]  font-semibold hover:underline flex items-center gap-2"
        >
          Back to Strategic Pillars
        </Button>
        <div className="flex items-center gap-3">
          <Button
            type="button"
            variant="secondary"
            loading={isBusy}
            onClick={handleSubmit((data) => submitWithStatus(data, 'DRAFT'))}
          >
            Save Draft
          </Button>
          <Button
            type="button"
            loading={isBusy}
            onClick={handleSubmit((data) => submitWithStatus(data, 'PUBLISH'))}
          >
            Publish
          </Button>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-6 py-8 space-y-6">
      <div>
        <Heading level={3} className=" leading-[1.1] font-semibold text-[#0F172A]">
          {isEditMode ? 'Edit Strategic Pillar' : 'Create Strategic Pillar'}
        </Heading>
        <nav className="mt-2 flex items-center gap-2 text-sm">
          <button
            type="button"
            onClick={() => navigate('/admin/strategic-pillars')}
            className="text-wine-100 hover:text-wine-200 hover:underline"
          >
            Strategic Pillars
          </button>
          {isEditMode ? (
            <>
              <span className="text-gray-400">/</span>
              <button
                type="button"
                onClick={() => navigate(`/admin/strategic-pillars/${pillarId}`)}
                className="text-wine-100 hover:text-wine-200 hover:underline"
              >
                {(watchTitle || '').trim() || 'Strategic Pillar'}
              </button>
              <span className="text-gray-400">/</span>
              <span className="text-gray-600">Edit</span>
            </>
          ) : (
            <>
              <span className="text-gray-400">/</span>
              <span className="text-gray-600">Create</span>
            </>
          )}
        </nav>
      </div>
      {loadingPillar && isEditMode ? (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-10">
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-[#047857]"></div>
            <Paragraph className="mt-4 text-sm text-gray-500">Loading strategic pillar...</Paragraph>
          </div>
        </div>
      ) : (
        <form className="space-y-6" onSubmit={(e) => e.preventDefault()}>
          <section className="rounded-xl border border-gray-200 bg-white p-5">
            <Heading level={4} className="mb-4 text-lg font-semibold text-gray-900">1. Basic Information</Heading>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div className="space-y-8">
                <Controller
                  name="title"
                  control={control}
                  render={({ field }) => (
                    <Input
                      label="Title"
                      required
                      value={field.value}
                      onChange={field.onChange}
                      error={errors.title?.message ? String(errors.title.message) : undefined}
                      $marginbottom="0"
                      placeholder="Enter title"
                    />
                  )}
                />
                <Controller
                  name="subtitle"
                  control={control}
                  render={({ field }) => (
                    <Input
                      label="Subtitle"
                      value={field.value}
                      onChange={field.onChange}
                      error={errors.subtitle?.message ? String(errors.subtitle.message) : undefined}
                      $marginbottom="0"
                      placeholder="Enter subtitle"
                    />
                  )}
                />
              </div>
              <Controller
                name="summary"
                control={control}
                render={({ field }) => (
                  <TextArea
                    label="Summary"
                    value={field.value}
                    onChange={field.onChange}
                    error={errors.summary?.message ? String(errors.summary.message) : undefined}
                    maxLength={300}
                    $marginbottom="0"
                    textAreaClassName="bg-[var(--inputBgColor)] min-h-[8rem] h-full text-text-primary"
                    placeholder="Enter a concise summary"
                    containerClassName='h-full'
                  />
                )}
              />
            </div>
          </section>
          <section className="rounded-xl border border-gray-200 bg-white p-5">
            <Heading level={4} className="mb-4 text-lg font-semibold text-gray-900 after:content-['*'] after:ml-0.5 after:text-red-500">2. Cover Image</Heading>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div className="rounded-lg border border-gray-200 overflow-hidden bg-gray-50 max-h-[220px]">
                {displayCoverImageUrl ? (
                  <img src={displayCoverImageUrl} alt="Cover" className="h-full w-full object-cover" />
                ) : (
                  <div className="h-full min-h-[220px] flex items-center justify-center text-sm text-gray-500">
                    <Paragraph className="text-sm text-gray-500">No image selected</Paragraph>
                  </div>
                )}
              </div>
              <div>
                <FileUpload
                  ref={fileUploadRef}
                  label=""
                  placeholder=""
                  $maxSizeMB={10}
                  multiple={false}
                  showPreview={false}
                  allowedFileTypes={['image/*']}
                  onChange={handleCoverFileChange}
                  error={errors.coverImageUrl?.message ? String(errors.coverImageUrl.message) : undefined}
                  containerClassName="border border-dashed border-gray-300 min-h-[220px]"
                >
                  <div className="flex h-full min-h-[180px] flex-col items-center justify-center gap-2 text-center">
                    <Paragraph className="text-sm font-semibold text-[#334155]">Upload cover image</Paragraph>
                    <Paragraph className="text-xs ">Drag and drop or click to browse</Paragraph>
                    <Paragraph className="text-xs ">Recommended: (JPG, PNG, WEBP)</Paragraph>
                    <Paragraph className="text-xs ">Max file size: 10MB</Paragraph>
                  </div>
                </FileUpload>
                <div className="mt-4 flex items-center gap-3">
                  <Button type="button" variant="secondary" onClick={handleBrowseFile}>
                    Select Image
                  </Button>
                  {localCoverFile && (
                    <Button type="button" variant="danger" onClick={handleLocalRemove}>
                      Remove
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </section>
                    <section className="rounded-xl  border border-gray-200 bg-white p-5">
            <Heading level={4} className="mb-4 text-lg font-semibold text-gray-900 after:content-['*'] after:ml-0.5 after:text-red-500">3. Article Body</Heading>
            <Controller
              name="content"
              control={control}
              render={({ field }) => (
                <div className="relative">
                  <RichTextEditor
{...field}
required
                    error={errors.content?.message ? String(errors.content.message) : undefined}
                    // onChange={field.onChange}
                    placeholder="Write strategic pillar content..."
                  />
               
                </div>
              )}
            />
          </section>
          <section className="rounded-xl border border-gray-200 bg-white p-5">
            <Heading level={4} className="mb-4 text-lg font-semibold text-gray-900">4. Metadata</Heading>
            <div className="grid items-end grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
              <Controller
                name="language"
                control={control}
                render={({ field }) => (
                  <div>

                    <CustomSelect
                    label="Language"
                      value={field.value}
                      onChange={field.onChange}
                      options={languageOptions}
                      placeholder="Select language"
                      error={errors.language?.message ? String(errors.language.message) : undefined}
                      isClearable
                      isSearchable
                      required
                    />
                 
                  </div>
                )}
              />
              <Controller
                name="articleTag"
                control={control}
                render={({ field }) => (
                  <div>
         
                    <CustomSelect
                {...field}
                      label="Tags"
                      value={parseTagString(field.value || '')}
                      onChange={(selectedValues) => {
                        const values = Array.isArray(selectedValues) ? selectedValues : [];
                        field.onChange(values.join(', '));
                      }}
                      options={[]}
                      placeholder="Add tags"
                      isMulti
                      isSearchable
                      isClearable
                      isCreatable
                      multiValueLayout="scroll"
                    />
                  </div>
                )}
              />
            </div>
          </section>
          <section className="rounded-xl border border-gray-300 px-4 py-6 flex flex-col gap-4">
            <Controller
              name="landingPagePrioritiesOfAfsa"
              control={control}
              render={({ field }) => (
                <Toggle
                  checked={Boolean(field.value)}
                  onChange={field.onChange}
                  label="Landing Page Priorities of AFSA"
                  description="Show this pillar on the landing page priorities section."
                />
              )}
            />
            <Controller
              name="whoWeAre"
              control={control}
              render={({ field }) => (
                <Toggle
                  checked={Boolean(field.value)}
                  onChange={field.onChange}
                  label="Who We Are"
                  description="Highlight this pillar in the 'Who We Are' section."
                />
              )}
            />
            <Controller
              name="memberCountries"
              control={control}
              render={({ field }) => (
                <Toggle
                  checked={Boolean(field.value)}
                  onChange={field.onChange}
                  label="Member Countries"
                  description="Highlight this pillar in the 'Member Countries' section."
                />
              )}
            />
     
            <Controller
              name="history"
              control={control}
              render={({ field }) => (
                <Toggle
                  checked={Boolean(field.value)}
                  onChange={field.onChange}
                  label="History"
                  description="Highlight this pillar in the 'History' section."
                />
              )}
            />
            <Controller
              name="leadership"
              control={control}
              render={({ field }) => (
                <Toggle
                  checked={Boolean(field.value)}
                  onChange={field.onChange}
                  label="Leadership"
                  description="Highlight this pillar in the 'Leadership' section."
                />
              )}
            />
          </section>

        </form>
      )}
      </div>
      <SaveDraftModal
        isOpen={showUnsavedModal}
        onClose={() => setShowUnsavedModal(false)}
        onConfirm={() => {
          setShowUnsavedModal(false);
          handleSubmit((data) => submitWithStatus(data, 'DRAFT'))();
        }}
        onDiscard={() => {
          setShowUnsavedModal(false);
          navigate('/admin/strategic-pillars');
        }}
        title="Save Pillar?"
        subtitle="You have unsaved changes. Save as draft before leaving?"
        confirmButtonText="Save as Draft"
        discardButtonText="Discard"
      />
    </div>
  );
};

export default StrategicPillarEditor;
